/**
 * CaseFlow+AI - Dialog Component
 * Provides Radix UI-style Dialog API for modals
 */

import React from 'react';

interface DialogProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: React.ReactNode;
}

interface DialogContentProps {
  className?: string;
  children: React.ReactNode;
}

interface DialogHeaderProps {
  children: React.ReactNode;
}

interface DialogTitleProps {
  children: React.ReactNode;
}

interface DialogFooterProps {
  children: React.ReactNode;
}

export function Dialog({ open, onOpenChange, children }: DialogProps) {
  if (!open) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={() => onOpenChange?.(false)}
      />
      {children}
    </div>
  );
}

export function DialogContent({ className = '', children }: DialogContentProps) {
  return (
    <div className={`
      relative w-full max-w-lg max-h-[90vh] rounded-3xl
      border border-gray-200 bg-white shadow-2xl
      dark:border-white/10 dark:bg-[#0f0f11]
      flex flex-col overflow-hidden
      ${className}
    `}>
      {children}
    </div>
  );
}

export function DialogHeader({ children }: DialogHeaderProps) {
  return (
    <div className="flex-shrink-0 p-6 pb-0">
      {children}
    </div>
  );
}

export function DialogTitle({ children }: DialogTitleProps) {
  return (
    <h2 className="text-xl font-bold text-gray-900 dark:text-white">
      {children}
    </h2>
  );
}

export function DialogFooter({ children }: DialogFooterProps) {
  return (
    <div className="flex-shrink-0 p-6 pt-4 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-[#0f0f11]">
      <div className="flex justify-end gap-3">
        {children}
      </div>
    </div>
  );
}
